import { useEffect, useState } from 'react';
import { Banknote, CheckCircle2, Clock, Landmark, RotateCcw, XCircle } from 'lucide-react';
import { congregationService } from '@/services/api';

const fmt = (n) => new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));

const EMPTY = { holder_name: '', doc_type: 'J', doc_number: '', bank_code: '', account_number: '', phone: '', email: '' };

export const STATUS_META = {
  pending_bank: { label: 'En el banco', cls: 'bg-amber-100 text-amber-800', Icon: Clock },
  success: { label: 'Confirmado', cls: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 },
  failed: { label: 'Rechazado (saldo devuelto)', cls: 'bg-red-100 text-red-800', Icon: XCircle },
  reversed: { label: 'Revertido (saldo devuelto)', cls: 'bg-red-100 text-red-800', Icon: RotateCcw },
};

export function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: status, cls: 'bg-gray-100 text-gray-700', Icon: Clock };
  const { Icon } = meta;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${meta.cls}`}>
      <Icon className="h-3 w-3" />{meta.label}
    </span>
  );
}

export function WithdrawModal({ congregation, onClose, onDone }) {
  const [account, setAccount] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    congregationService.getBankAccount(congregation.project_id)
      .then((d) => {
        setAccount(d.bank_account);
        if (d.bank_account) setForm({ ...EMPTY, ...d.bank_account });
        setEditing(!d.registered);
      })
      .catch(() => setEditing(true));
  }, [congregation.project_id]);

  const saveAccount = async () => {
    setError(''); setBusy(true);
    try {
      const d = await congregationService.saveBankAccount(congregation.project_id, form);
      setAccount(d.bank_account);
      setEditing(false);
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo guardar la cuenta.');
    } finally { setBusy(false); }
  };

  const withdraw = async () => {
    setError('');
    const value = Number(amount);
    if (!value || value <= 0) { setError('Indica un monto mayor a 0.'); return; }
    if (value > Number(congregation.balance || 0)) { setError('El monto supera el saldo disponible.'); return; }
    setBusy(true);
    try {
      const d = await congregationService.requestWithdrawal(congregation.project_id, value);
      setResult(d);
      onDone?.();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo procesar el retiro.');
    } finally { setBusy(false); }
  };

  const Field = ({ label, k, placeholder, mono }) => (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
      <input
        value={form[k] || ''} placeholder={placeholder} data-testid={`withdraw-account-${k}`}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        className={`w-full rounded-lg border border-gray-300 px-3 py-2 text-sm ${mono ? 'font-mono' : ''}`}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="withdraw-modal">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-2xl">
        <div className="mb-1 flex items-center gap-2 text-lg font-bold" style={{ color: '#051C2C' }}>
          <Banknote className="h-5 w-5" /> Retiro · {congregation.name}
        </div>
        <p className="mb-4 text-sm text-gray-500">
          Saldo disponible <span className="font-semibold text-gray-800">Bs. {fmt(congregation.balance)}</span>
        </p>

        {error ? (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-red-700" data-testid="withdraw-error">{error}</div>
        ) : null}

        {result ? (
          <div className="space-y-3" data-testid="withdraw-result">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
              Retiro de <strong>Bs. {fmt(result.amount)}</strong> enviado al banco.
              <div className="mt-1"><StatusPill status={result.status} /></div>
              {result.bank_reference ? (
                <p className="mt-2 text-xs">Referencia bancaria: <code className="font-mono">{result.bank_reference}</code></p>
              ) : null}
              {result.bank_error ? <p className="mt-2 text-xs text-red-700">{result.bank_error}</p> : null}
              <p className="mt-2 text-xs text-emerald-800">
                El saldo queda retenido y se devuelve automáticamente si el banco no ejecuta el débito.
              </p>
            </div>
            <button onClick={onClose} className="w-full rounded-lg bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300" data-testid="withdraw-close">
              Cerrar
            </button>          </div>
        ) : (
          <>
            <div className="mb-4 rounded-lg border border-gray-200 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
                  <Landmark className="h-4 w-4" /> Cuenta destino
                </span>
                {account && !editing ? (
                  <button onClick={() => setEditing(true)} data-testid="withdraw-edit-account" className="text-xs font-medium text-blue-700 hover:underline">
                    Cambiar
                  </button>
                ) : null}
              </div>
              {account && !editing ? (
                <div className="text-sm text-gray-700" data-testid="withdraw-account-summary">
                  <p className="font-medium">{account.holder_name}</p>
                  <p className="font-mono text-xs text-gray-500">
                    {account.doc_type}-{account.doc_number} · {account.account_number}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Field label="Titular" k="holder_name" placeholder="Nombre como aparece en el banco" /></div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Tipo doc.</label>
                    <select
                      value={form.doc_type} data-testid="withdraw-account-doc_type"
                      onChange={(e) => setForm({ ...form, doc_type: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    >
                      {['V', 'E', 'J', 'G', 'P'].map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <Field label="Número doc." k="doc_number" placeholder="408320126" mono />
                  <Field label="Código banco" k="bank_code" placeholder="0138" mono />
                  <Field label="Cuenta (20 dígitos)" k="account_number" placeholder="01380010360100346480" mono />
                  <Field label="Teléfono (opcional)" k="phone" placeholder="04141234567" mono />
                  <Field label="Correo (opcional)" k="email" placeholder="tesoreria@iglesia.com" />
                  <div className="col-span-2">
                    <button
                      onClick={saveAccount} disabled={busy} data-testid="withdraw-save-account"
                      className="w-full rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                      style={{ backgroundColor: '#003594' }}
                    >
                      {busy ? 'Guardando…' : 'Guardar cuenta'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {account && !editing ? (
              <>
                <label className="mb-1 block text-xs font-medium text-gray-600">Monto a retirar (Bs.)</label>
                <div className="mb-2 flex gap-2">
                  <input
                    type="number" step="0.01" value={amount} data-testid="withdraw-amount"
                    onChange={(e) => setAmount(e.target.value)}
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    placeholder="0,00"
                  />
                  <button
                    onClick={() => setAmount(String(congregation.balance))} data-testid="withdraw-use-all"
                    className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Todo
                  </button>
                </div>
                {congregation.bank_fee_passthrough && account.bank_code && account.bank_code !== '0138' && (
                  <p className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="withdraw-interbank-notice">
                    Dispersión a otro banco: se descuenta <strong>0,3% adicional</strong> de fee interbancario sobre el monto retirado.
                    {Number(amount) > 0 && <> Para retirar Bs. {fmt(amount)} necesitas Bs. {fmt(Number(amount) * 1.003)} de saldo.</>}
                  </p>
                )}
                <div className="flex gap-3 pt-2">
                  <button onClick={onClose} className="flex-1 rounded-lg border border-gray-300 px-4 py-2 hover:bg-gray-50" data-testid="withdraw-cancel">Cancelar</button>
                  <button
                    onClick={withdraw} disabled={busy} data-testid="withdraw-confirm"
                    className="flex-1 rounded-lg px-4 py-2 font-medium text-white disabled:opacity-60"
                    style={{ backgroundColor: '#003594' }}
                  >
                    {busy ? 'Enviando al banco…' : 'Retirar ahora'}
                  </button>
                </div>
              </>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

export function WithdrawalsHistory({ reloadKey }) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    congregationService.listWithdrawals()
      .then((d) => setRows(d.withdrawals || []))
      .catch(() => setRows([]));
  }, [reloadKey]);

  if (!rows.length) return null;

  return (
    <div className="mt-6 rounded-xl border border-gray-200 bg-white shadow-sm" data-testid="withdrawals-history">
      <div className="border-b border-gray-200 px-6 py-4 text-lg font-semibold" style={{ color: '#051C2C' }}>
        Retiros
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left text-gray-500">
              <th className="px-6 py-3 font-medium">Fecha</th>
              <th className="px-6 py-3 font-medium">Congregación</th>
              <th className="px-6 py-3 text-right font-medium">Monto (Bs.)</th>
              <th className="px-6 py-3 font-medium">Cuenta</th>
              <th className="px-6 py-3 font-medium">Estado</th>
              <th className="px-6 py-3 font-medium">Referencia</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((w) => (
              <tr key={w.withdrawal_id} data-testid={`withdrawal-row-${w.withdrawal_id}`}>
                <td className="px-6 py-3 text-gray-500">
                  {w.created_at ? new Date(w.created_at).toLocaleString('es-VE') : '—'}
                </td>
                <td className="px-6 py-3 font-mono text-xs">{w.congregation_code || '—'}</td>
                <td className="px-6 py-3 text-right font-semibold">{fmt(w.amount)}</td>
                <td className="px-6 py-3 font-mono text-xs text-gray-500">···{w.destination_account}</td>
                <td className="px-6 py-3"><StatusPill status={w.status} /></td>
                <td className="px-6 py-3 font-mono text-xs text-gray-500">{w.bank_reference || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
